<template>
  <div
    v-show="hasVisibleSection"
    class="terminal-environment-summary"
    :class="{
      'is-cordova-android': isCordovaAndroid,
      'has-device-battery': displayDeviceBattery
    }"
  >
    <!-- 终端时间与天气共用一个紧凑信息组，避免标题栏出现多个分散区域。 -->
    <div v-if="showTime" class="terminal-environment-clock" :title="$t(`${dateText} ${timeText}`)">
      <strong>{{ $t(timeText) }}</strong>
      <small>{{ $t(dateText) }}</small>
    </div>
    <span v-if="showTime && showWeather" class="terminal-environment-divider terminal-environment-weather-divider" aria-hidden="true"></span>
    <!-- 天气面板即使被隐藏也保持挂载，继续向地图天气图层提供实时数据。 -->
    <div v-show="showWeather" class="terminal-environment-weather">
      <WeatherPanel
        ref="weatherPanel"
        variant="ground-station"
        :shop="shop"
        data-terminal-feedback="weather"
        :class="{ 'terminal-control-click-flash': flashing }"
        use-shop-prop
        @change="$emit('weather-change', $event)"
      />
    </div>
    <template v-if="displayDeviceBattery">
      <span v-if="showTime || showWeather" class="terminal-environment-divider terminal-environment-battery-divider" aria-hidden="true"></span>
      <div
        class="terminal-device-battery"
        :class="deviceBatteryTone"
        :title="$t(deviceBatteryTitle)"
        :aria-label="$t('安卓设备电量')"
      >
        <span class="terminal-device-battery-shell" aria-hidden="true">
          <span class="terminal-device-battery-fill" :style="{ width: `${deviceBatteryLevel}%` }"></span>
        </span>
        <span v-if="deviceBatteryCharging" class="terminal-device-battery-charging" aria-hidden="true">ϟ</span>
        <strong>{{ deviceBatteryLevel }}%</strong>
      </div>
    </template>
  </div>
</template>

<script>
import WeatherPanel from '@/components/WeatherPanel'
import { getRuntimePlatform } from '@/utils/runtimePlatform'

let lastDeviceBatteryStatus = null

export default {
  name: 'TerminalEnvironmentSummary',
  components: { WeatherPanel },
  data () {
    return {
      isCordovaAndroid: false,
      deviceBatteryLevel: null,
      deviceBatteryCharging: false,
      batteryListenerActive: false
    }
  },
  props: {
    now: { type: Date, required: true },
    shop: { type: Object, default: null },
    flashing: { type: Boolean, default: false },
    showTime: { type: Boolean, default: true },
    showWeather: { type: Boolean, default: true },
    showDeviceBattery: { type: Boolean, default: true }
  },
  computed: {
    timeText () {
      return this.now.toLocaleTimeString(this.$locale, { hour12: false })
    },
    dateText () {
      return this.now.toLocaleDateString(this.$locale, { month: '2-digit', day: '2-digit', weekday: 'short' })
    },
    hasDeviceBattery () {
      return this.isCordovaAndroid && Number.isFinite(this.deviceBatteryLevel)
    },
    displayDeviceBattery () {
      return this.showDeviceBattery && this.hasDeviceBattery
    },
    hasVisibleSection () {
      return this.showTime || this.showWeather || this.displayDeviceBattery
    },
    deviceBatteryTone () {
      if (this.deviceBatteryLevel <= 20) return 'is-danger'
      if (this.deviceBatteryLevel <= 40) return 'is-warning'
      return 'is-normal'
    },
    deviceBatteryTitle () {
      return `设备电量 ${this.deviceBatteryLevel}%${this.deviceBatteryCharging ? '，充电中' : ''}`
    }
  },
  mounted () {
    this.isCordovaAndroid = this.detectCordovaAndroid()
    if (this.isCordovaAndroid && lastDeviceBatteryStatus) this.handleBatteryStatus(lastDeviceBatteryStatus)
    document.addEventListener('deviceready', this.handleDeviceReady, false)
    // 插件事件通道已注册时直接订阅；否则等待 deviceready，避免冷启动时监听被替换。
    if (this.isCordovaAndroid && navigator.battery?.channels?.batterystatus) {
      this.startBatteryMonitor()
    }
  },
  beforeDestroy () {
    document.removeEventListener('deviceready', this.handleDeviceReady, false)
    if (this.batteryListenerActive) {
      window.removeEventListener('batterystatus', this.handleBatteryStatus, false)
    }
  },
  methods: {
    refreshWeather () {
      const panel = this.$refs.weatherPanel
      return panel && typeof panel.refreshWeather === 'function'
        ? panel.refreshWeather()
        : Promise.resolve()
    },
    detectCordovaAndroid () {
      return getRuntimePlatform().isCordovaAndroid
    },
    handleDeviceReady () {
      this.isCordovaAndroid = this.detectCordovaAndroid()
      if (this.isCordovaAndroid) this.startBatteryMonitor()
    },
    startBatteryMonitor () {
      if (this.batteryListenerActive) return
      window.addEventListener('batterystatus', this.handleBatteryStatus, false)
      this.batteryListenerActive = true
      // Cordova only emits when the value changes; a remounted header needs its last known value.
      const battery = navigator.battery
      if (battery && Number.isFinite(battery._level)) {
        this.handleBatteryStatus({ level: battery._level, isPlugged: battery._isPlugged })
      }
    },
    handleBatteryStatus (status = {}) {
      const level = Number(status.level)
      if (status.level == null || !Number.isFinite(level)) return
      this.deviceBatteryLevel = Math.min(100, Math.max(0, Math.round(level)))
      this.deviceBatteryCharging = status.isPlugged === true
      lastDeviceBatteryStatus = { level: this.deviceBatteryLevel, isPlugged: this.deviceBatteryCharging }
    }
  }
}
</script>

<style lang="scss" scoped>
.terminal-environment-summary {
  height: 40px;
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm);
  padding: 0 var(--ui-space-sm);
  color: var(--ui-text-primary);
  border: 1px solid var(--ui-border-color-subtle);
  border-radius: 6px;
  background: rgba(var(--ui-bg-control-rgb), .72);
  box-sizing: border-box;
  white-space: nowrap;
}

.terminal-environment-clock {
  min-width: 73px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.15;
}

.terminal-environment-clock strong {
  font-family: Consolas, monospace;
  font-size: 14px;
  letter-spacing: .8px;
}

.terminal-environment-clock small {
  margin-top: 2px;
  color: var(--ui-text-secondary);
  font-size: var(--gs-font-micro, 10px);
}

.terminal-environment-divider {
  width: 1px;
  height: 22px;
  flex: none;
  background: var(--ui-border-color-subtle);
}

.terminal-environment-summary ::v-deep .weather-panel {
  height: 38px;
}

.terminal-environment-weather {
  display: flex;
  align-items: center;
}

.terminal-environment-summary ::v-deep .weather-trigger {
  height: 38px;
  padding: 0;
}

.terminal-device-battery {
  min-width: 53px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--ui-color-primary);
}

.terminal-device-battery.is-warning { color: var(--ui-color-warning); }
.terminal-device-battery.is-danger { color: var(--ui-color-danger); }

.terminal-device-battery-shell {
  width: 22px;
  height: 11px;
  position: relative;
  display: block;
  padding: 2px;
  border: 1px solid currentColor;
  border-radius: 3px;
  box-sizing: border-box;
}

.terminal-device-battery-shell::after {
  width: 2px;
  height: 5px;
  position: absolute;
  top: 2px;
  right: -4px;
  content: '';
  border-radius: 0 2px 2px 0;
  background: currentColor;
}

.terminal-device-battery-fill {
  height: 100%;
  display: block;
  border-radius: 1px;
  background: currentColor;
}

.terminal-device-battery-charging {
  margin-left: -2px;
  color: var(--ui-color-warning);
  font-size: 12px;
  line-height: 1;
}

.terminal-device-battery strong {
  font-family: Consolas, monospace;
  font-size: 12px;
  line-height: 1;
}

/* 手机超窄屏才退化为仅显示电量；Pad 保留完整环境信息。 */
</style>
